import React from "react";
import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import { store } from "../redux/store";
import { getActionColors } from "./theme/actionColors";

// One shared pill-shaped toast — colored circular icon, bold title, a
// smaller message line under it — used for every action across the app
// (add/edit/delete/renew/sync/etc, success or failure) via
// notify()/notifySuccess()/notifyError()/etc below. Redesigned to match a
// reference "rounded pill notification" set: one colored badge per kind
// (success/info/warning/error), tinted background to match.
const ICONS = { success: CheckRoundedIcon, error: CloseRoundedIcon, warning: PriorityHighRoundedIcon, info: LightbulbRoundedIcon };
const TITLES = { success: "Success!", error: "Something Went Wrong!", warning: "Warning!", info: "Did You Know?" };

const isDarkNow = () => {
  try { return store.getState().ui.mode === "dark"; } catch (_) { return false; }
};

const colorFor = (kind, C) => (kind === "success" ? C.success : kind === "error" ? C.error : kind === "warning" ? C.warning : C.info);

const ActionToastBody = ({ kind, message }) => {
  const C = getActionColors(isDarkNow());
  const color = colorFor(kind, C);
  const Icon = ICONS[kind] || ICONS.info;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, py: 0.2 }}>
      <Box
        sx={{
          width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
          background: color,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 4px 12px ${color}55`,
          animation: "csToastPop 0.4s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        <Icon sx={{ fontSize: 20, color: "#fff" }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: "0.86rem", fontWeight: 800, color: `${C.text} !important`, lineHeight: 1.25 }}>
          {TITLES[kind] || TITLES.info}
        </Typography>
        <Typography sx={{ fontSize: "0.76rem", color: `${C.muted} !important`, lineHeight: 1.3, mt: 0.1 }}>
          {message}
        </Typography>
      </Box>
    </Box>
  );
};

const show = (kind, message, options = {}) => {
  const C = getActionColors(isDarkNow());
  const color = colorFor(kind, C);
  toast(
    <ActionToastBody kind={kind} message={message} />,
    {
      position: "top-right",
      autoClose: kind === "error" ? 6000 : 4000,
      closeButton: false,
      // Solid white background + a colored border (not a tinted/soft
      // background) — a tinted pill nearly disappeared against busy page
      // backgrounds (e.g. the auth pages' promo photo), so solid white
      // keeps it readable everywhere while the border still carries the
      // success/error/warning/info color.
      style: { background: "#fffcf7", border: `1.5px solid ${color}`, borderRadius: "18px", boxShadow: "0 8px 24px rgba(111,82,18,0.16)", padding: "10px 12px" },
      ...options,
    }
  );
};

/** Generic — pick your own message and severity. */
export const notify = (kind, message, options) => show(kind, message, options);

export const notifySuccess = (message, options) => show("success", message, options);
export const notifyError = (message, options) => show("error", message, options);
export const notifyWarning = (message, options) => show("warning", message, options);
export const notifyInfo = (message, options) => show("info", message, options);

/** Consistent wording helpers for the three CRUD actions. */
export const notifyAdded = (entity = "Item", options) => show("success", `${entity} added successfully`, options);
export const notifyUpdated = (entity = "Item", options) => show("success", `${entity} updated successfully`, options);
export const notifyDeleted = (entity = "Item", options) => show("success", `${entity} deleted successfully`, options);
export const notifyActionError = (action, fallback = "Something went wrong", err, options) =>
  show("error", err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback, options);
